import ExcelJS from 'exceljs';
import { CargoEventReportData } from '../../ReportGeneratorTypes';
import { BerthCallInfo } from '../../../../types/ViewModel/BerthCallInfo';
import { TankersEvent } from '../../../../types/ViewModel/TankersEvent';
import { setCellValueWithFont } from './reportEventExcelUtils';
import { ExcelReportInfoBM } from 'src/types/BusinessModel/ExcelReportInfoBM';
import { Vessel } from 'src/types/ViewModel/Vessel/Vessel';
import { EventTypeLevel } from '../../../../types/ViewModel/EventTypeLevel';
import { addEventLines } from './parts/rows';
import { addHeadingPort, addHeadingBerth, addHeadingCargoOp, addHeadingCargoOpWithLaytime } from './parts/headings';
import {
    NodeIdMap,
    itemExists,
    itemExistsInCommaDelimitedString,
    getTranshipment,
    createAdjustedTabName,
    filterCargoOpForCharterer,
    getBerthName,
    copySheet,
    getNorTenderEvent,
    getCargoOperationFromStowage,
    getChartererName,
    getCargoName,
    getCargoNumber,
} from './parts/helpers';
import { RotationPortCall } from 'src/types/ViewModel/Stowage/Rotation/RotationPortCall';
import { CargoReportEnum } from 'src/enums/cargoReportEnum';

const initializeEventHeader = (worksheet: ExcelJS.Worksheet, rowNum: number) => {
    setCellValueWithFont(worksheet, rowNum, 1, 'Timestamp', { bold: true, color: '#fff', backgroundColor: '#4682B4' });
    setCellValueWithFont(worksheet, rowNum, 2, 'Activity', { bold: true, color: '#fff', backgroundColor: '#4682B4' });
    setCellValueWithFont(worksheet, rowNum, 3, 'Comments', { bold: true, color: '#fff', backgroundColor: '#4682B4' });
    // Merge C and D for the header
    worksheet.mergeCells(rowNum, 3, rowNum, 4);
};

const createNodeIdMapBerthCall = (bcEvList: TankersEvent[], selectedBerthCallIdList: BerthCallInfo[]): NodeIdMap[] => {
    const nodeIdMap: NodeIdMap[] = [];

    // Get unique berth call IDs from events
    const uniqueBerthCallIds = Array.from(new Set(bcEvList.map((ev) => ev.berthCallId).filter((id) => id !== null && id !== undefined)));

    for (const bcId of uniqueBerthCallIds) {
        const bcEv = bcEvList.find((o) => o.berthCallId === bcId && o.berthId !== null && o.berthId !== undefined);
        if (bcEv && bcEv.berthId) {
            const bc = selectedBerthCallIdList.find((o) => o.berthCallId === bcEv.berthCallId);
            if (bc) {
                const berthIdStr = bcEv.berthId.toString();
                const adjustedName = createAdjustedTabName(bc.name);

                const sameBerthIdList = nodeIdMap.filter((o) => o.rotationNodeId === berthIdStr || o.adjustedName === adjustedName);

                let seq: number | null = null;
                if (sameBerthIdList.length > 0) {
                    if (sameBerthIdList.length === 1) {
                        const firstItem = nodeIdMap.find((o) => o.rotationNodeId === berthIdStr || o.adjustedName === adjustedName);
                        if (firstItem) {
                            firstItem.seqId = 1;
                        }
                    }
                    seq = sameBerthIdList.length + 1;
                }

                nodeIdMap.push({
                    berthCallId: bcId,
                    cargoOpId: null,
                    rotationNodeId: berthIdStr,
                    adjustedName: adjustedName,
                    seqId: seq,
                });
            }
        }
    }

    return nodeIdMap;
};

const createNodeIdMapCargoOp = (
    coEvList: TankersEvent[],
    stowagePlan: any,
    chNameList: string,
    nodeIdMapListCargoOp: NodeIdMap[],
    bcId: number | null
): NodeIdMap[] => {
    // Get unique cargo operation IDs
    const coIdList = Array.from(new Set(coEvList.map((x) => x.cargoOperationId).filter((id) => id !== null && id !== undefined))) as number[];

    const filteredCoIdList = filterCargoOpForCharterer(stowagePlan, coIdList, chNameList);

    for (const coId of filteredCoIdList) {
        const event = coEvList.find((o) => o.cargoOperationId === coId);
        const cargoOpNumber = event?.cargoNumber;

        if (cargoOpNumber && cargoOpNumber.trim() !== '') {
            const sameCargoOpIdList = nodeIdMapListCargoOp.filter((o) => o.rotationNodeId === cargoOpNumber);
            let seq: number | null = null;

            if (sameCargoOpIdList.length > 0) {
                if (sameCargoOpIdList.length === 1) {
                    const firstItem = nodeIdMapListCargoOp.find((o) => o.rotationNodeId === cargoOpNumber);
                    if (firstItem) {
                        firstItem.seqId = 1;
                    }
                }
                seq = sameCargoOpIdList.length + 1;
            }

            if (bcId !== null && bcId !== undefined) {
                nodeIdMapListCargoOp.push({
                    berthCallId: bcId,
                    cargoOpId: coId,
                    rotationNodeId: cargoOpNumber,
                    adjustedName: null,
                    seqId: seq,
                });
            }
        }
    }

    return nodeIdMapListCargoOp;
};

const getCargoNumberFromNodeMap = (ev: TankersEvent | null, nodeIdMapListCargoOp: NodeIdMap[]): string => {
    if (!ev || !ev.cargoOperationId) return 'Unknown';

    const nodeIdMap = nodeIdMapListCargoOp.find((o) => o.cargoOpId === ev.cargoOperationId);
    if (nodeIdMap) {
        return getBerthName(nodeIdMap.rotationNodeId, nodeIdMap);
    }

    return ev.cargoNumber || 'Unknown';
};

// moved to helpers

const addCargoOpEvents = (
    workbook: ExcelJS.Workbook,
    excelReportInfo: ExcelReportInfoBM,
    portCall: RotationPortCall,
    vessel: Vessel,
    portName: string,
    berthName: string,
    evList: TankersEvent[],
    evTypeLevelList: EventTypeLevel[],
    nodeIdMapListCargoOp: NodeIdMap[],
    norTenderEvent: TankersEvent | null
): void => {
    const firstEv = evList.length > 0 ? evList[0] : null;
    const templateSheet = workbook.worksheets[0];
    const newSheetName = getCargoNumberFromNodeMap(firstEv, nodeIdMapListCargoOp);
    const sheet = copySheet(workbook, templateSheet, newSheetName);
    setDefaultColumnWidths(sheet);

    if (sheet && firstEv) {
        const cargoOp = getCargoOperationFromStowage(portCall, firstEv);
        let nrOfRows = 0;

        if (excelReportInfo.reportType === CargoReportEnum.CargoEventWithLaytime) {
            // CargoEventWithLaytime enum value
            let demurrageRate: number | null = null;
            let pumpingRate: number | null = null;
            const cargoOperationList = portCall?.berthCalls?.flatMap((bc) => bc.cargoOperations) ?? [];
            if (cargoOperationList && firstEv.cargoOperationId) {
                const orcaCargoOp = cargoOperationList.find((op) => op.imosCargoOperationId === firstEv.cargoOperationId);
                if (orcaCargoOp) {
                    demurrageRate = orcaCargoOp.demurrageRatePerDay;
                    pumpingRate = orcaCargoOp.pumpingRatePerHour;
                }
            }

            nrOfRows = addHeadingCargoOpWithLaytime(
                sheet,
                portCall,
                evList,
                portName,
                vessel,
                berthName,
                getChartererName(cargoOp) || '',
                getCargoName(cargoOp),
                getCargoNumber(cargoOp),
                norTenderEvent,
                demurrageRate,
                pumpingRate
            );
            initializeEventHeader(sheet, nrOfRows);
        } else {
            nrOfRows = addHeadingCargoOp(
                sheet,
                portCall,
                evList,
                portName,
                vessel,
                berthName,
                getChartererName(cargoOp) || '',
                getCargoName(cargoOp),
                getCargoNumber(cargoOp)
            );
            initializeEventHeader(sheet, nrOfRows);
        }

        addEventLines(sheet, evList, evTypeLevelList, nrOfRows);
    }
};

const addBerthEvents = (
    workbook: ExcelJS.Workbook,
    excelReportInfo: ExcelReportInfoBM,
    portCall: RotationPortCall,
    vessel: Vessel,
    portName: string,
    nodeIdMap: NodeIdMap,
    evList: TankersEvent[],
    evTypeLevelList: EventTypeLevel[],
    nodeIdMapListCargoOp: NodeIdMap[]
): NodeIdMap[] => {
    // Filter events for this berth call without cargo operations
    const bcEvList = evList.filter((o) => o.berthCallId === nodeIdMap.berthCallId && !o.cargoOperationId);

    // All events for this berth call
    const bcEvListAll = evList.filter((o) => o.berthCallId === nodeIdMap.berthCallId);

    const berthHasTranshipment = getTranshipment(bcEvListAll);
    const firstBcEv = bcEvListAll.length > 0 ? bcEvListAll[0] : null;
    const templateSheet = workbook.worksheets[0];

    if (firstBcEv && firstBcEv.berthCallId) {
        const bc = excelReportInfo.selectedBerthCallIdList.find((o) => o.berthCallId === firstBcEv.berthCallId);

        if (bc) {
            const newSheetName = getBerthName(bc.name, nodeIdMap);
            const sheet = copySheet(workbook, templateSheet, newSheetName);
            setDefaultColumnWidths(sheet);

            if (sheet) {
                // Add berth heading and events
                const nrOfRows = addHeadingBerth(
                    sheet,
                    portCall,
                    excelReportInfo,
                    bcEvList,
                    bc.name,
                    vessel,
                    berthHasTranshipment,
                    firstBcEv.voyageNumber || '',
                    firstBcEv.berthCallId
                );
                initializeEventHeader(sheet, nrOfRows);

                addEventLines(sheet, bcEvList, evTypeLevelList, nrOfRows);

                // Filter cargo operation events for this berth
                const coEvList = evList.filter(
                    (ev) =>
                        firstBcEv.berthCallId === ev.berthCallId &&
                        itemExistsInCommaDelimitedString(excelReportInfo.cargoOperationIdList, ev.cargoOperationId)
                );

                // Get unique cargo operation IDs
                let coIdList = Array.from(
                    new Set(coEvList.filter((x) => x.cargoOperationId !== null && x.cargoOperationId !== undefined).map((x) => x.cargoOperationId!))
                );

                // Filter by charterer
                coIdList = filterCargoOpForCharterer(portCall, coIdList, excelReportInfo.chartererNameList || '');

                // Update cargo operation node map
                const updatedNodeIdMapListCargoOp = createNodeIdMapCargoOp(
                    coEvList,
                    portCall,
                    excelReportInfo.chartererNameList || '',
                    nodeIdMapListCargoOp,
                    firstBcEv.berthCallId
                );

                // Process each cargo operation
                for (const coId of coIdList) {
                    const norTenderEvent = getNorTenderEvent(evList, coId);
                    const cargoEvList = evList.filter((o) => o.cargoOperationId === coId);

                    addCargoOpEvents(
                        workbook,
                        excelReportInfo,
                        portCall,
                        vessel,
                        portName,
                        bc.name,
                        cargoEvList,
                        evTypeLevelList,
                        updatedNodeIdMapListCargoOp,
                        norTenderEvent
                    );
                }

                return updatedNodeIdMapListCargoOp;
            }
        }
    }

    return nodeIdMapListCargoOp;
};

const setDefaultColumnWidths = (templateSheet: ExcelJS.Worksheet) => {
    templateSheet.getColumn(1).width = 18;
    templateSheet.getColumn(2).width = 26;
    templateSheet.getColumn(3).width = 30;
    templateSheet.getColumn(4).width = 17;
};

export const generateCargoEventReport = (workBook: ExcelJS.Workbook, cargoEventReportData: CargoEventReportData) => {
    // Filter: remove deleted events and sea events (equivalent to evListNotDeleted)
    const evListNotDeleted = cargoEventReportData.tankersEvents.filter((ev) => !ev.deleted);

    const bcEvList = evListNotDeleted.filter((ev) => itemExists(cargoEventReportData.excelReportInfo.selectedBerthCallIdList, ev.berthCallId));
    const pcEvList = evListNotDeleted.filter((o) => o.portCallId === cargoEventReportData.excelReportInfo.portCallId && !o.berthCallId);
    const pcEvListAll = evListNotDeleted.filter((o) => o.portCallId === cargoEventReportData.excelReportInfo.portCallId);
    const portHasTranshipment = getTranshipment(pcEvListAll);
    const portName = cargoEventReportData.selectedPortCall.portDisplayName;

    // Set the first worksheet name to the port name
    const templateSheet = workBook.addWorksheet(portName);
    setDefaultColumnWidths(templateSheet);

    const nodeIdMapListBerthCall = createNodeIdMapBerthCall(bcEvList, cargoEventReportData.excelReportInfo.selectedBerthCallIdList);
    const evTypeLevelList = cargoEventReportData.eventTypeData.eventTypeLevelArray;

    let nodeIdMapListCargoOp: NodeIdMap[] = [];

    // Process each berth call - equivalent to the foreach loop in C#
    for (const nodeIdBerthCall of nodeIdMapListBerthCall) {
        nodeIdMapListCargoOp = addBerthEvents(
            workBook,
            cargoEventReportData.excelReportInfo,
            cargoEventReportData.selectedPortCall,
            cargoEventReportData.vessel,
            portName,
            nodeIdBerthCall,
            evListNotDeleted,
            evTypeLevelList,
            nodeIdMapListCargoOp
        );
    }

    // Add port-level heading and events to the main template sheet
    const nrOfRows = addHeadingPort(
        templateSheet,
        cargoEventReportData.stowagePlan,
        cargoEventReportData.excelReportInfo,
        pcEvList,
        cargoEventReportData.selectedPortCall,
        cargoEventReportData.vessel,
        portHasTranshipment
    );

    initializeEventHeader(templateSheet, nrOfRows);

    // Add port-level event lines (start inserting after the 'Timestamp' row)
    addEventLines(templateSheet, pcEvList, evTypeLevelList, nrOfRows);
};
